<template>
  <section class="invoice-page" aria-label="发票记录">
    <div class="invoice-table-viewport">
      <div class="invoice-table" role="table" aria-label="发票记录列表">
        <div class="invoice-table__header" role="row">
          <span v-for="column in columns" :key="column.key" role="columnheader">
            {{ column.label }}
          </span>
        </div>

        <div class="invoice-table__body" role="rowgroup" @scroll="handleListScroll">
          <template v-if="invoiceRows.length">
            <div v-for="row in invoiceRows" :key="row.key" class="invoice-table__row" role="row">
              <span role="cell" :title="row.appliedAt">{{ row.appliedAt }}</span>
              <span role="cell">{{ row.amount }}</span>
              <span role="cell">{{ row.status }}</span>

              <div class="invoice-table__cell" role="cell">
                <button
                  v-if="row.hasExpressInfo"
                  type="button"
                  class="invoice-action-button"
                  @click="openInvoiceDialog('express', row.source)"
                >
                  查看信息
                </button>
                <span v-else>无</span>
              </div>

              <div class="invoice-table__cell invoice-order-cell" role="cell">
                <span :title="row.orderId">{{ row.orderId }}</span>
                <el-popover
                  v-if="row.showAllOrderIds"
                  placement="top"
                  :width="320"
                  trigger="click"
                  popper-class="invoice-order-popover"
                >
                  <template #reference>
                    <button type="button" class="invoice-more-button">更多</button>
                  </template>
                  <div class="invoice-order-popover__content">
                    <span v-for="orderId in row.orderIds" :key="orderId">{{ orderId }}</span>
                  </div>
                </el-popover>
              </div>

              <div class="invoice-table__cell" role="cell">
                <button
                  type="button"
                  class="invoice-action-button"
                  @click="openInvoiceDialog('content', row.source)"
                >
                  查看内容
                </button>
              </div>

              <div class="invoice-table__cell" role="cell">
                <button
                  type="button"
                  class="invoice-action-button"
                  @click="openInvoiceDialog('details', row.source)"
                >
                  查看详情
                </button>
              </div>
            </div>
          </template>

          <div v-else-if="initialLoading" class="invoice-table__state" role="status">
            <SvgIcon name="refresh" :size="22" color="#6033e1" loading />
            <span>正在加载发票记录...</span>
          </div>

          <div v-else-if="loadError" class="invoice-table__state" role="status">
            <SvgIcon name="orders-empty" :size="60" color="#6033e1" />
            <span>{{ loadError }}</span>
            <button
              type="button"
              class="invoice-retry-button"
              @click="loadInvoiceHistory({ reset: true })"
            >
              重新加载
            </button>
          </div>

          <div v-else class="invoice-table__state" role="status">
            <SvgIcon name="orders-empty" :size="60" color="#6033e1" />
            <span>暂无发票记录</span>
          </div>

          <div v-if="invoiceRows.length && loading" class="invoice-table__load-state" role="status">
            <SvgIcon name="refresh" :size="16" loading />
            <span>正在加载更多...</span>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="dialogVisible"
      class="invoice-detail-dialog"
      width="520px"
      align-center
      append-to-body
      :show-close="false"
      @closed="clearInvoiceDialog"
    >
      <template #header>
        <div class="invoice-dialog__header">
          <h2>{{ dialogTitle }}</h2>
          <button type="button" aria-label="关闭" @click="dialogVisible = false">
            <SvgIcon name="modal-close" :size="12" />
          </button>
        </div>
      </template>

      <dl class="invoice-dialog__fields">
        <div v-for="field in dialogFields" :key="field.label" class="invoice-dialog__field">
          <dt>{{ field.label }}</dt>
          <dd>{{ field.value }}</dd>
        </div>
      </dl>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { ElMessage } from 'element-plus';
  import {
    fetchInvoiceOrders,
    fetchInvoiceHistory,
    INVOICE_HISTORY_PAGE_SIZE,
    type InvoiceHistoryItem,
    type InvoiceOrderRecord
  } from '@/api/invoice';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';

  type InvoiceDialogType = 'express' | 'content' | 'details';

  interface TableColumn {
    key: string;
    label: string;
  }

  interface InvoiceRow {
    key: string;
    appliedAt: string;
    amount: string;
    status: string;
    hasExpressInfo: boolean;
    orderId: string;
    orderIds: string[];
    showAllOrderIds: boolean;
    source: InvoiceHistoryItem;
  }

  interface InvoiceDialogField {
    label: string;
    value: string;
  }

  const columns: TableColumn[] = [
    { key: 'appliedAt', label: '时间' },
    { key: 'amount', label: '发票金额' },
    { key: 'status', label: '发票状态' },
    { key: 'express', label: '快递信息' },
    { key: 'orderId', label: '订单号' },
    { key: 'content', label: '发票内容' },
    { key: 'details', label: '发票详情' }
  ];

  const invoiceRows = ref<InvoiceRow[]>([]);
  const currentPage = ref(0);
  const pageSize = INVOICE_HISTORY_PAGE_SIZE;
  const total = ref(0);
  const loading = ref(false);
  const hasMore = ref(true);
  const loadError = ref('');
  const dialogVisible = ref(false);
  const dialogTitle = ref('');
  const dialogFields = ref<InvoiceDialogField[]>([]);
  let latestRequestId = 0;
  let latestDialogRequestId = 0;

  const initialLoading = computed(() => loading.value && invoiceRows.value.length === 0);

  onMounted(() => {
    void loadInvoiceHistory({ reset: true });
  });

  async function loadInvoiceHistory({ reset = false }: { reset?: boolean } = {}) {
    if (!reset && (loading.value || !hasMore.value)) return;

    const requestId = latestRequestId + 1;
    const page = reset ? 1 : currentPage.value + 1;
    latestRequestId = requestId;
    loading.value = true;
    loadError.value = '';

    if (reset) {
      currentPage.value = 0;
      total.value = 0;
      hasMore.value = true;
      invoiceRows.value = [];
    }

    try {
      const result = await fetchInvoiceHistory({
        current: page,
        size: pageSize
      });

      if (requestId !== latestRequestId) return;

      currentPage.value = result.current;
      total.value = result.total;
      const nextRows = result.list.map((item, index) => mapInvoiceRow(item, index, page));
      invoiceRows.value = reset ? nextRows : [...invoiceRows.value, ...nextRows];
      hasMore.value =
        result.total > 0
          ? invoiceRows.value.length < result.total
          : result.list.length >= result.size;
    } catch (error) {
      if (requestId !== latestRequestId) return;

      const message = getErrorMessage(error, '发票记录加载失败，请稍后重试');
      loadError.value = message;
      console.error('获取发票记录失败', error);
      ElMessage.error(message);
    } finally {
      if (requestId === latestRequestId) loading.value = false;
    }
  }

  function mapInvoiceRow(item: InvoiceHistoryItem, index: number, page: number): InvoiceRow {
    const orderIds = splitOrderIds(item.orderId);

    return {
      key:
        item.id === null
          ? `${item.orderId || 'invoice'}-${page}-${index}`
          : `${item.id}-${item.orderId}`,
      appliedAt: formatInvoiceDate(item.newTime),
      amount: `¥${item.amount}`,
      status: formatInvoiceStatus(item.status),
      hasExpressInfo: hasExpressInformation(item),
      orderId: item.orderId || '-',
      orderIds,
      showAllOrderIds: orderIds.length > 1 || item.orderId.length > 30,
      source: item
    };
  }

  function splitOrderIds(orderId: string) {
    const orderIds = orderId
      .split(/[，,、\s]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    return orderIds.length ? orderIds : ['-'];
  }

  function hasExpressInformation(item: InvoiceHistoryItem) {
    return (
      item.express === 1 || Boolean(item.addressee || item.address || item.phone || item.postcode)
    );
  }

  function formatInvoiceDate(value: unknown) {
    if (value === undefined || value === null || value === '') return '-';
    if (typeof value !== 'string' || !Number.isNaN(Number(value))) {
      const timestamp = Number(value);

      if (Number.isFinite(timestamp) && timestamp > 0) {
        const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
        const date = new Date(normalizedTimestamp);

        if (!Number.isNaN(date.getTime())) {
          return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
        }
      }
    }

    const stringValue = String(value);
    return stringValue.split(/[ T]/)[0] || stringValue;
  }

  function formatInvoiceStatus(status: number | null) {
    return status === 1 ? '已处理' : '待处理';
  }

  function formatInvoiceType(type: number | null) {
    if (type === 1) return '增值税普通发票';
    if (type === 4) return '增值税专用发票';
    return type === null ? '-' : String(type);
  }

  function openInvoiceDialog(type: InvoiceDialogType, item: InvoiceHistoryItem) {
    const dialogRequestId = latestDialogRequestId + 1;
    latestDialogRequestId = dialogRequestId;

    if (type === 'express') {
      dialogTitle.value = '快递信息';
      dialogFields.value = [
        createDialogField('收件人', item.addressee),
        createDialogField('联系电话', item.phone),
        createDialogField('收件地址', item.address),
        createDialogField('邮政编码', item.postcode)
      ];
    } else if (type === 'content') {
      dialogTitle.value = '发票内容';
      dialogFields.value = [
        createDialogField('发票类型', formatInvoiceType(item.iType)),
        createDialogField('发票抬头', item.title),
        createDialogField('税号', item.number),
        createDialogField('备注', item.remarks)
      ];
    } else {
      void openInvoiceDetailsDialog(item, dialogRequestId);
      return;
    }

    dialogVisible.value = true;
  }

  async function openInvoiceDetailsDialog(item: InvoiceHistoryItem, requestId: number) {
    const invoiceId = item.id;

    if (invoiceId === null) {
      ElMessage.warning('未找到发票编号');
      return;
    }

    dialogTitle.value = '发票详情';
    dialogFields.value = createInvoiceDetailFields(item, []);
    dialogVisible.value = true;

    try {
      const orders = await fetchInvoiceOrders(invoiceId);
      if (requestId !== latestDialogRequestId) return;

      dialogFields.value = createInvoiceDetailFields(item, orders);
    } catch (error) {
      if (requestId !== latestDialogRequestId) return;

      const message = getErrorMessage(error, '发票详情加载失败，请稍后重试');
      console.error('获取发票详情失败', error);
      ElMessage.error(message);
      dialogFields.value = createInvoiceDetailFields(item, []);
    }
  }

  function createInvoiceDetailFields(item: InvoiceHistoryItem, orders: InvoiceOrderRecord[]) {
    const firstOrder = orders[0];

    return [
      createDialogField('发票编号', item.id),
      createDialogField('申请时间', formatInvoiceDate(firstOrder?.new_time ?? item.newTime)),
      createDialogField('订单号', formatInvoiceOrderIds(orders, item.orderId)),
      createDialogField('发票金额', `¥${formatInvoiceAmount(firstOrder?.amount ?? item.amount)}`),
      createDialogField('发票状态', formatInvoiceStatus(item.status)),
      createDialogField('收票邮箱', item.reEmail),
      createDialogField('联系电话', item.rePhone),
      createDialogField('注册地址及电话', item.regieterAddressTel),
      createDialogField('注册电话', item.regieterTel),
      createDialogField('开户银行', item.bankOpen),
      createDialogField('银行账号', item.bankAccount)
    ];
  }

  function formatInvoiceOrderIds(orders: InvoiceOrderRecord[], fallback: string) {
    const orderIds = orders
      .map((order) => order.id)
      .filter((value) => value !== undefined && value !== null && value !== '')
      .map(String);

    return orderIds.length ? orderIds.join('，') : fallback;
  }

  function formatInvoiceAmount(value: unknown) {
    if (value === undefined || value === null || value === '') return '0.00';

    const numberValue = Number(value);
    if (Number.isFinite(numberValue)) return numberValue.toFixed(2);

    return String(value);
  }

  function createDialogField(label: string, value: unknown): InvoiceDialogField {
    const displayValue =
      value === undefined || value === null || value === '' ? '-' : String(value);
    return { label, value: displayValue };
  }

  function pad(value: number) {
    return String(value).padStart(2, '0');
  }

  function clearInvoiceDialog() {
    latestDialogRequestId += 1;
    dialogTitle.value = '';
    dialogFields.value = [];
  }

  function handleListScroll(event: Event) {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom > 48 || loading.value || !hasMore.value) return;

    void loadInvoiceHistory();
  }

  function getErrorMessage(error: unknown, fallback: string) {
    if (typeof error === 'object' && error !== null) {
      const errorRecord = error as Record<string, unknown>;
      if (typeof errorRecord.errorMsg === 'string' && errorRecord.errorMsg) {
        return errorRecord.errorMsg;
      }
      if (typeof errorRecord.msg === 'string' && errorRecord.msg) return errorRecord.msg;
      if (typeof errorRecord.message === 'string' && errorRecord.message) {
        return errorRecord.message;
      }
    }

    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }
</script>

<style scoped>
  .invoice-page {
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow: hidden;
    display: flex;
    color: #160211;
  }

  .invoice-table-viewport {
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .invoice-table {
    width: 100%;
    min-width: 1240px;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    font-size: 14px;
  }

  .invoice-table__header,
  .invoice-table__row {
    display: grid;
    grid-template-columns: 13.54fr 14.18fr 14.51fr 15.8fr 21.63fr 15.28fr 5.06fr;
    align-items: center;
    padding: 0 32px;
    box-sizing: border-box;
  }

  .invoice-table__header {
    flex-shrink: 0;
    height: 44px;
    margin-right: 8px;
    border-radius: 12px;
    background: #f2eff9;
    color: #6e6a7c;
    line-height: 20px;
  }

  .invoice-table__header span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .invoice-table__body {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-gutter: stable;
    overscroll-behavior-y: contain;
  }

  .invoice-table__body::-webkit-scrollbar {
    width: 8px;
  }

  .invoice-table__body::-webkit-scrollbar-track {
    background: transparent;
  }

  .invoice-table__body::-webkit-scrollbar-thumb {
    border: 2px solid transparent;
    border-radius: 8px;
    background: #d9d4e6;
    background-clip: padding-box;
  }

  .invoice-table__body::-webkit-scrollbar-thumb:hover {
    background: #bdb5d1;
    background-clip: padding-box;
  }

  .invoice-table__row {
    min-height: 84px;
    border-bottom: 1px solid #eff0f7;
    line-height: 22px;
  }

  .invoice-table__row > span,
  .invoice-table__cell {
    min-width: 0;
  }

  .invoice-table__row > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .invoice-table__cell {
    display: flex;
    align-items: center;
  }

  .invoice-order-cell {
    padding-right: 18px;
    gap: 4px;
  }

  .invoice-order-cell > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .invoice-action-button {
    width: 80px;
    height: 36px;
    padding: 0 12px;
    border: 0;
    border-radius: 8px;
    background: rgba(96, 51, 225, 0.08);
    color: #6033e1;
    font: inherit;
    line-height: 14px;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s;
  }

  .invoice-action-button:hover {
    background: rgba(96, 51, 225, 0.14);
  }

  .invoice-more-button {
    flex-shrink: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6033e1;
    font: inherit;
    line-height: 14px;
    cursor: pointer;
  }

  .invoice-action-button:focus-visible,
  .invoice-more-button:focus-visible,
  .invoice-retry-button:focus-visible,
  .invoice-dialog__header button:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .invoice-table__state {
    min-height: 220px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #6e6a7c;
    font-size: 14px;
  }

  .invoice-retry-button {
    height: 36px;
    padding: 0 16px;
    border: 0;
    border-radius: 8px;
    background: rgba(96, 51, 225, 0.08);
    color: #6033e1;
    font: inherit;
    cursor: pointer;
  }

  .invoice-table__load-state {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #8b8798;
    font-size: 13px;
  }

  .invoice-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .invoice-dialog__header h2 {
    margin: 0;
    color: #160211;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  .invoice-dialog__header button {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #8b8798;
    cursor: pointer;
  }

  .invoice-dialog__header button:hover {
    background: #f5f3fd;
    color: #6033e1;
  }

  .invoice-dialog__fields {
    margin: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 24px;
  }

  .invoice-dialog__field {
    min-width: 0;
  }

  .invoice-dialog__field dt {
    margin-bottom: 6px;
    color: #8b8798;
    font-size: 13px;
    line-height: 20px;
  }

  .invoice-dialog__field dd {
    margin: 0;
    overflow-wrap: anywhere;
    color: #160211;
    font-size: 14px;
    line-height: 22px;
  }

  :global(.invoice-detail-dialog) {
    max-width: calc(100vw - 32px);
    padding: 0;
    overflow: hidden;
    border-radius: 16px;
  }

  :global(.invoice-detail-dialog .el-dialog__header) {
    margin: 0;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #eff0f7;
  }

  :global(.invoice-detail-dialog .el-dialog__body) {
    max-height: min(520px, calc(100vh - 180px));
    padding: 24px;
    overflow-y: auto;
  }

  :global(.invoice-order-popover .invoice-order-popover__content) {
    max-height: 180px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    color: #160211;
    font-size: 14px;
    line-height: 22px;
    overflow-wrap: anywhere;
  }

  @media (max-width: 767px) {
    .invoice-table {
      min-width: 1080px;
    }

    .invoice-table__header,
    .invoice-table__row {
      padding: 0 20px;
    }

    .invoice-dialog__fields {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
